import React, { useState, useRef, useEffect } from 'react';
import styles from './QuizPhoneInput.module.scss';
import classNames from 'classnames';
import ArrowIcon from '../../../assets/icons/arrowBtn.svg';
import ArrowDownIcon from '../../../assets/icons/arrowDown.svg';
import { countryPickerData } from '../../../config/countryData';
import { CountryData } from '../../../types';
// Import flag icons from country-flag-icons
import DE from 'country-flag-icons/react/3x2/DE';
import AT from 'country-flag-icons/react/3x2/AT';
import CH from 'country-flag-icons/react/3x2/CH';
import NL from 'country-flag-icons/react/3x2/NL';
import BE from 'country-flag-icons/react/3x2/BE';
import FR from 'country-flag-icons/react/3x2/FR';
import IT from 'country-flag-icons/react/3x2/IT';
import PL from 'country-flag-icons/react/3x2/PL';
import ES from 'country-flag-icons/react/3x2/ES';
import GB from 'country-flag-icons/react/3x2/GB';

// Map country codes to flag components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const flagComponents: Record<string, any> = {
  DE,
  AT,
  CH,
  NL,
  BE,
  FR,
  IT,
  PL,
  ES,
  GB,
};

// Helper component to render flag
const FlagIcon: React.FC<{ countryCode: string; className?: string }> = ({
  countryCode,
  className,
}) => {
  const FlagComponent = flagComponents[countryCode];
  if (!FlagComponent) return null;
  return <FlagComponent className={className} />;
};

interface QuizPhoneInputProps {
  value?: string;
  onSubmit: (value: string) => void;
  onSkip?: () => void;
  showSkip?: boolean;
}

const QuizPhoneInput: React.FC<QuizPhoneInputProps> = ({
  value: initialValue = '',
  onSubmit,
  onSkip,
  showSkip = true,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryData>(
    countryPickerData[0] // Germany by default
  );
  const [phoneNumber, setPhoneNumber] = useState(initialValue);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= selectedCountry.maxLength) {
      setPhoneNumber(value);
      if (error) setError('');
    }
  };

  const handleCountrySelect = (country: CountryData) => {
    setSelectedCountry(country);
    setShowDropdown(false);
    // Clear phone if it exceeds new country's max length
    if (phoneNumber.length > country.maxLength) {
      setPhoneNumber(phoneNumber.slice(0, country.maxLength));
    }
  };

  const handleSubmit = () => {
    if (phoneNumber.trim()) {
      const fullNumber = `+${selectedCountry.callingCode}${phoneNumber}`;
      onSubmit(fullNumber);
    } else if (onSkip) {
      onSkip();
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      onSubmit('-');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className={styles.quizPhoneInput}>
      <div className={styles.inputWrapper}>
        <div className={styles.phoneContainer} ref={dropdownRef}>
          {/* Country Selector */}
          <button
            className={styles.countrySelector}
            onClick={() => setShowDropdown(!showDropdown)}
            type="button"
          >
            <FlagIcon countryCode={selectedCountry.key} className={styles.flagIcon} />
            <span>+{selectedCountry.callingCode}</span>
            <img src={ArrowDownIcon} alt="" className={styles.arrowDown} />
          </button>

          {/* Country Dropdown */}
          {showDropdown && (
            <div className={styles.countryDropdown}>
              {countryPickerData.map((country) => (
                <button
                  key={country.key}
                  className={classNames(styles.countryOption, {
                    [styles.selected]: country.key === selectedCountry.key,
                  })}
                  onClick={() => handleCountrySelect(country)}
                  type="button"
                >
                  <FlagIcon countryCode={country.key} className={styles.flagIcon} />
                  <span className={styles.countryName}>{country.name_DE}</span>
                  <span className={styles.callingCode}>+{country.callingCode}</span>
                </button>
              ))}
            </div>
          )}

          {/* Phone Input */}
          <input
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            onKeyDown={handleKeyDown}
            placeholder="Telefonnummer"
            maxLength={selectedCountry.maxLength}
            className={error ? styles.hasError : ''}
          />
        </div>

        <button className={styles.submitBtn} onClick={handleSubmit} type="button">
          <img src={ArrowIcon} alt="Submit" />
        </button>
      </div>

      {error && <span className={styles.error}>{error}</span>}

      {showSkip && (
        <button className={styles.skipBtn} onClick={handleSkip} type="button">
          Überspringen
        </button>
      )}
    </div>
  );
};

export default QuizPhoneInput;
